import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Line, ScanFields as ScanFieldsType } from '../types';
import { useSubinventoryLov } from '../hooks/useSubinventoryLov';
import { useLocatorLov } from '../hooks/useLocatorLov';
import { useSupportedItemList } from '../hooks/useSupportedItemList';

interface NewLineFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newLine: Partial<Line>) => void;
  scanFields: ScanFieldsType;
  isDarkTheme?: boolean;
  selectedHeaderToOrg?: string;
  selectedHeaderFromSite?: string;
  serialNumberFromLines?: string; // Add this prop to pass the correct serial number
}

const NewLineForm: React.FC<NewLineFormProps> = ({
  isOpen,
  onClose,
  onSave,
  scanFields,
  isDarkTheme = false,
  selectedHeaderToOrg,
  selectedHeaderFromSite,
  serialNumberFromLines
}) => {
  const [formData, setFormData] = useState<Partial<Line>>({
    description: '',
    serialNumber: scanFields.scanSerialNumber,
    assetNumber: '',
    validatedSerialNumber: scanFields.scanSerialNumber,
    validatedAssetNumber: '',
    itemNumber: '',
    toSubinventory: scanFields.toSubinventory,
    toLocator: scanFields.toLocator,
    trackingNumber: scanFields.trackingNumber,
    internalSalesOrder: '',
    isoLineNumber: '',
    repairEligibility: '',
    status: 'New'
  });
  
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const itemTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const { data: subinventories, refetch: refetchSubinventories } = useSubinventoryLov(selectedHeaderToOrg, false);
  const { data: locators, refetch: refetchLocators } = useLocatorLov(selectedHeaderToOrg, formData.toSubinventory, false);
  const { data: supportedItems, isLoading: itemsLoading, fetchSupportedItems } = useSupportedItemList();

  const filteredItems = React.useMemo(() => {
    if (!itemSearch.trim()) return supportedItems;
    const searchTerm = itemSearch.toLowerCase();
    return supportedItems.filter(item => 
      item.itemNumber.toLowerCase().includes(searchTerm) ||
      item.mfgPartNumber.toLowerCase().includes(searchTerm) ||
      item.itemDescription.toLowerCase().includes(searchTerm)
    );
  }, [supportedItems, itemSearch]);

  useEffect(() => {
    if (isOpen) {
      // Use serialNumberFromLines if provided, otherwise use scanned serial
      const serialNumber = serialNumberFromLines || scanFields.scanSerialNumber;
      
      // Reset form data with current scan fields when form opens
      setFormData({
        description: '',
        serialNumber: serialNumber,
        assetNumber: '',
        validatedSerialNumber: scanFields.scanSerialNumber,
        validatedAssetNumber: '',
        itemNumber: '',
        toSubinventory: scanFields.toSubinventory,
        toLocator: scanFields.toLocator,
        trackingNumber: scanFields.trackingNumber,
        internalSalesOrder: '',
        isoLineNumber: '',
        repairEligibility: 'Yes',
        status: 'New'
      });
      
      // Clear errors when form opens
      setFieldErrors({});
      
      // Center the popup when it opens
      setPosition({ x: 0, y: 0 });
      
      if (selectedHeaderToOrg) {
        refetchSubinventories(selectedHeaderToOrg);
      }
    }
  }, [isOpen, selectedHeaderToOrg, refetchSubinventories, scanFields, serialNumberFromLines]);

  // Clear field errors when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest('input') && !target.closest('select') && !target.closest('[data-item-dropdown]')) {
        setFieldErrors({});
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart.x, dragStart.y]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove]);

  useEffect(() => {
    if (selectedHeaderToOrg && formData.toSubinventory) {
      refetchLocators(selectedHeaderToOrg, formData.toSubinventory);
    }
  }, [formData.toSubinventory, selectedHeaderToOrg, refetchLocators]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-item-dropdown]')) {
        setShowItemDropdown(false);
        setItemSearch('');
      }
    };

    if (showItemDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showItemDropdown]);

  const handleInputChange = (field: keyof Line, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateField = (fieldName: string, value: string | undefined): string => {
    let mandatoryFields = ['serialNumber', 'validatedSerialNumber', 'toSubinventory', 'repairEligibility'];
    
    // Item Number is only mandatory if Repair Eligibility is not "No"
    if (formData.repairEligibility !== 'No') {
      mandatoryFields.push('itemNumber');
    }
    
    if (mandatoryFields.includes(fieldName) && (!value || value.trim() === '')) {
      const displayName = fieldName === 'serialNumber' ? 'Serial Number' :
                         fieldName === 'validatedSerialNumber' ? 'Validated Serial Number' :
                         fieldName === 'itemNumber' ? 'Item Number' :
                         fieldName === 'toSubinventory' ? 'To Subinventory' :
                         fieldName === 'repairEligibility' ? 'Repair Eligibility' : fieldName;
      return `${displayName} is mandatory and cannot be null`;
    }
    return '';
  };

  const handleSave = () => {
    const errors: Record<string, string> = {};
    let mandatoryFields = ['serialNumber', 'validatedSerialNumber', 'toSubinventory', 'repairEligibility'];
    
    // Item Number is only mandatory if Repair Eligibility is not "No"
    if (formData.repairEligibility !== 'No') {
      mandatoryFields.push('itemNumber');
    }
    
    mandatoryFields.forEach(field => {
      const error = validateField(field, formData[field as keyof Line] as string);
      if (error) {
        errors[field] = error;
      }
    });
    
    setFieldErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  };

  const modalStyle = {
    backgroundColor: isDarkTheme ? '#2a2a2a' : '#fff',
    color: isDarkTheme ? '#fff' : '#000',
    padding: '15px',
    borderRadius: '8px',
    width: '600px',
    maxHeight: '90vh',
    overflowY: 'auto' as const,
    border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
    position: 'relative' as const
  };

  const getInputStyle = (fieldName: string) => ({
    width: '100%',
    padding: '6px',
    border: `2px solid ${fieldErrors[fieldName] ? '#dc3545' : (isDarkTheme ? '#555' : '#ccc')}`,
    borderRadius: '4px',
    backgroundColor: isDarkTheme ? '#333' : '#fff',
    color: isDarkTheme ? '#fff' : '#000',
    fontSize: '12px',
    boxSizing: 'border-box' as const
  });

  const errorStyle = {
    color: '#dc3545',
    fontSize: '10px',
    marginTop: '1px',
    height: '12px'
  };

  return (
    <div style={overlayStyle}>
      <div style={{
        ...modalStyle,
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}>
        <h3 onMouseDown={handleMouseDown} style={{ cursor: 'grab', userSelect: 'none' }}>Serial Exception</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Repair Eligibility: <span style={{ color: 'black' }}>*</span></label>
            <select
              value={formData.repairEligibility || ''}
              onChange={(e) => handleInputChange('repairEligibility', e.target.value)}
              style={{...getInputStyle('repairEligibility'), width: '120px', height: '28px'}}
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.repairEligibility || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Description:</label>
            <input
              type="text"
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              style={{...getInputStyle('description'), width: '300px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.description || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Serial Number: <span style={{ color: 'black' }}>*</span></label>
            <input
              type="text"
              value={formData.serialNumber || ''}
              onChange={(e) => handleInputChange('serialNumber', e.target.value)}
              style={{...getInputStyle('serialNumber'), width: '150px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.serialNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Asset Number:</label>
            <input
              type="text"
              value={formData.assetNumber || ''}
              onChange={(e) => handleInputChange('assetNumber', e.target.value)}
              style={{...getInputStyle('assetNumber'), width: '150px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.assetNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Validated Serial Number: <span style={{ color: 'black' }}>*</span></label>
            <input
              type="text"
              value={formData.validatedSerialNumber || ''}
              onChange={(e) => handleInputChange('validatedSerialNumber', e.target.value)}
              style={{...getInputStyle('validatedSerialNumber'), width: '150px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.validatedSerialNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Validated Asset Number:</label>
            <input
              type="text"
              value={formData.validatedAssetNumber || ''}
              onChange={(e) => handleInputChange('validatedAssetNumber', e.target.value)}
              style={{...getInputStyle('validatedAssetNumber'), width: '150px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.validatedAssetNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Item Number: {formData.repairEligibility !== 'No' && <span style={{ color: 'black' }}>*</span>}</label>
            <div style={{ position: 'relative' }} data-item-dropdown>
              <input
                type="text"
                value={showItemDropdown ? itemSearch : formData.itemNumber || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (showItemDropdown) {
                    setItemSearch(value);
                    
                    // Clear previous timeout
                    if (itemTimeoutRef.current) {
                      clearTimeout(itemTimeoutRef.current);
                    }
                    
                    // Auto-select after user stops typing
                    if (value.trim()) {
                      itemTimeoutRef.current = setTimeout(() => {
                        // Check for exact match first
                        const exactMatch = supportedItems.find(item => 
                          item.itemNumber.toLowerCase() === value.toLowerCase() ||
                          item.mfgPartNumber.toLowerCase() === value.toLowerCase() ||
                          item.itemDescription.toLowerCase() === value.toLowerCase()
                        );
                        
                        if (exactMatch) {
                          handleInputChange('itemNumber', exactMatch.itemNumber);
                          handleInputChange('description', exactMatch.itemDescription);
                          handleInputChange('assetNumber', exactMatch.mfgPartNumber);
                          handleInputChange('repairEligibility', 'Yes');
                          setShowItemDropdown(false);
                          setItemSearch('');
                        } else {
                          // Check if only one match remains
                          const searchTerm = value.toLowerCase();
                          const filtered = supportedItems.filter(item => 
                            item.itemNumber.toLowerCase().includes(searchTerm) ||
                            item.mfgPartNumber.toLowerCase().includes(searchTerm) ||
                            item.itemDescription.toLowerCase().includes(searchTerm)
                          );
                          
                          if (filtered.length === 1) {
                            handleInputChange('itemNumber', filtered[0].itemNumber);
                            handleInputChange('description', filtered[0].itemDescription);
                            handleInputChange('assetNumber', filtered[0].mfgPartNumber);
                            handleInputChange('repairEligibility', 'Yes');
                            setShowItemDropdown(false);
                            setItemSearch('');
                          }
                        }
                      }, 500);
                    }
                  } else {
                    handleInputChange('itemNumber', value);
                    if (value.trim()) {
                      setItemSearch(value);
                      setShowItemDropdown(true);
                      if (selectedHeaderFromSite) {
                        fetchSupportedItems(selectedHeaderFromSite);
                      }
                    }
                  }
                }}
                onFocus={() => {
                  setItemSearch(formData.itemNumber || '');
                  setShowItemDropdown(true);
                  if (selectedHeaderFromSite) {
                    fetchSupportedItems(selectedHeaderFromSite);
                  }
                }}
                style={{...getInputStyle('itemNumber'), width: '120px', height: '28px'}}
              />
              {formData.itemNumber && (
                <span
                  onClick={() => {
                    handleInputChange('itemNumber', '');
                    setItemSearch('');
                    setShowItemDropdown(false);
                  }}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '8px',
                    cursor: 'pointer',
                    color: isDarkTheme ? '#ccc' : '#666',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    lineHeight: '1',
                    height: '20px',
                  }}
                >
                  ×
                </span>
              )}
              {showItemDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  backgroundColor: isDarkTheme ? '#333' : '#fff',
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
                  borderRadius: '4px',
                  zIndex: 1000,
                  width: '400px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {itemsLoading ? (
                    <div style={{ padding: '8px 12px', color: isDarkTheme ? '#fff' : '#000' }}>Loading...</div>
                  ) : filteredItems.length > 0 ? (
                    filteredItems.map((item) => (
                      <div
                        key={item.itemNumber}
                        onClick={() => {
                          handleInputChange('itemNumber', item.itemNumber);
                          handleInputChange('description', item.itemDescription);
                          handleInputChange('assetNumber', item.mfgPartNumber);
                          handleInputChange('repairEligibility', 'Yes');
                          setShowItemDropdown(false);
                          setItemSearch('');
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: isDarkTheme ? '#fff' : '#000',
                          borderBottom: `1px solid ${isDarkTheme ? '#555' : '#eee'}`,
                          fontSize: '12px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isDarkTheme ? '#444' : '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '4px', alignItems: 'center' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{item.itemNumber}</div>
                          <div style={{ fontSize: '13px', color: isDarkTheme ? '#ccc' : '#666' }}>{item.itemDescription}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '8px 12px', color: isDarkTheme ? '#fff' : '#000' }}>No items found</div>
                  )}
                </div>
              )}
            </div>
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.itemNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>To Subinventory: <span style={{ color: 'black' }}>*</span></label>
            <select
              value={formData.toSubinventory || ''}
              onChange={(e) => {
                handleInputChange('toSubinventory', e.target.value);
                handleInputChange('toLocator', ''); // Clear locator when subinventory changes
              }}
              style={{...getInputStyle('toSubinventory'), width: '200px', height: '28px'}}
            >
              <option value="">Select subinventory</option>
              {subinventories.map((subinventory) => (
                <option key={subinventory} value={subinventory}>
                  {subinventory}
                </option>
              ))}
            </select>
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.toSubinventory || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>To Locator:</label>
            <select
              value={formData.toLocator || ''}
              onChange={(e) => handleInputChange('toLocator', e.target.value)}
              disabled={!formData.toSubinventory}
              style={{...getInputStyle('toLocator'), width: '250px', height: '28px'}}
            >
              <option value="">Select locator</option>
              {locators.map((locator) => (
                <option key={locator} value={locator}>
                  {locator}
                </option>
              ))}
            </select>
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.toLocator || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Tracking Number:</label>
            <input
              type="text"
              value={formData.trackingNumber || ''}
              onChange={(e) => handleInputChange('trackingNumber', e.target.value)}
              style={{...getInputStyle('trackingNumber'), width: '150px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.trackingNumber || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>Internal Sales Order:</label>
            <input
              type="text"
              value={formData.internalSalesOrder || ''}
              onChange={(e) => handleInputChange('internalSalesOrder', e.target.value)}
              style={{...getInputStyle('internalSalesOrder'), width: '150px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.internalSalesOrder || ''}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ minWidth: '160px', fontSize: '12px', textAlign: 'right', lineHeight: '28px' }}>ISO Line Number:</label>
            <input
              type="text"
              value={formData.isoLineNumber || ''}
              onChange={(e) => handleInputChange('isoLineNumber', e.target.value)}
              style={{...getInputStyle('isoLineNumber'), width: '100px', height: '28px'}}
            />
            <div style={{...errorStyle, marginLeft: '10px'}}>{fieldErrors.isoLineNumber || ''}</div>
          </div>
        </div>
        <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: isDarkTheme ? '#555' : '#ccc',
              color: isDarkTheme ? '#fff' : '#000',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewLineForm;